import zod from 'zod'
import { Button, Form } from '@wellesley/ui'
import { FC } from 'react'
import { AppsLink } from '@wellesley/router'

import CommonFormField from 'components/form/CommonFormField'
import {
  passwordValidator,
  maxPwdLength,
  useCommonForm,
} from '@wellesley/forms'
import { useSubmit } from './useSubmit'
import { ForgotPasswordContainer } from '../ForgotPasswordContainer'
import { ForgotPasswordPending } from '../ForgotPasswordPending'

const formSchema = zod
  .object({
    newPassword: passwordValidator,
    confirmNewPassword: zod.string().trim(),
  })
  .superRefine(({ newPassword, confirmNewPassword }, ctx) => {
    if (confirmNewPassword !== newPassword) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'Passwords do not match',
        path: ['confirmNewPassword'],
      })
    }
  })

export type RegisterInput = zod.TypeOf<typeof formSchema>

export const ForgotPasswordConfirm: FC = () => {
  const form = useCommonForm({
    schema: formSchema,
    defaultValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
  })
  const { control, handleSubmit } = form
  const { isLoading, onSubmit } = useSubmit()

  if (isLoading) return <ForgotPasswordPending />

  return (
    <Form {...form}>
      <form onSubmit={(...args) => void handleSubmit(onSubmit)(...args)}>
        <ForgotPasswordContainer>
          <div>
            <div className="flex flex-col gap-s">
              <CommonFormField
                control={control}
                type="password"
                name="newPassword"
                displayName="Create new password"
                maxChars={maxPwdLength}
                description="Required: A-Z, a-z, 0-9, special character, min 8, max 64"
              />
              <CommonFormField
                control={control}
                type="password"
                name="confirmNewPassword"
                displayName="Confirm new password"
                maxChars={maxPwdLength}
              />
              <div className="text-center text-base font-normal text-gray-500">
                New sign-in will be required on all devices
              </div>
            </div>
            <div className="mt-m flex justify-stretch gap-xs">
              <Button asChild variant="secondary" size="m" className="flex-1">
                <AppsLink to="/signin">Cancel</AppsLink>
              </Button>
              <Button
                type="submit"
                size="m"
                variant="primary"
                className="flex-1"
                disabled={!form.formState.isValid}
              >
                Save
              </Button>
            </div>
          </div>
        </ForgotPasswordContainer>
      </form>
    </Form>
  )
}
